import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getTenant } from '../../api/client';
import { ApiError } from '../../api/envelope';
import type { CrmNotification, NotificationMute, NotificationPreferences } from '../../api/types';
import {
  DEFAULT_CALL_RINGTONE,
  DEFAULT_MESSAGE_TONE,
  isAlertTone,
  playTone,
  primeAudio,
} from '../../lib/sound';
import {
  notificationPermission,
  registerBackgroundPush,
  requestNotificationPermission,
  showSystemNotification,
  type PermissionState,
} from '../../lib/webNotify';
import { useComms } from './context';
import {
  NotificationsContext,
  notificationOrigin,
  notificationTitle,
  type NotificationsState,
} from './notifications';

/**
 * Per-agent notification state for the module: the unread badge, the bell
 * panel's list, the toast stack, the agent's mutes (ThreadPane renders the
 * per-conversation mute state from here), and the alert settings the bell and
 * the ringtone both read. Socket `notification.created` is a USER-ROOM event —
 * it only ever arrives for the signed-in agent, so nothing here filters by
 * recipient.
 *
 * Framed by the dispatch dashboard, the module shows no notifications of its
 * own — no bell, no toasts, no tone, no banner. The dashboard lists them in its
 * own bell (the CRM tab, read from the same `GET /notifications`) and toasts,
 * sounds and banners them on every page, so the console adding its own would
 * double every one of them on the one page where both are on screen. What still
 * runs here is everything that is not a display: the settings and mutes the
 * settings card and the thread read, marking a conversation's notifications
 * read when it is opened — relayed to the dashboard so its count drops too —
 * and the relay of each arrival. The incoming-call ringtone is the exception
 * and plays in both modes: nothing else covers calls (see IncomingCallOverlay).
 */

const PAGE_SIZE = 25;
const TOAST_MS = 8_000;
const MAX_TOASTS = 3;

export function NotificationsProvider({
  children,
  selectedId,
  onOpenConversation,
}: {
  children: ReactNode;
  /** The conversation open in the thread pane — its toasts are suppressed. */
  selectedId: number | null;
  onOpenConversation: (displayId: number) => void;
}) {
  const { source, refreshNonce } = useComms();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<CrmNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [denied, setDenied] = useState(false);
  const [toasts, setToasts] = useState<CrmNotification[]>([]);
  const [mutes, setMutes] = useState<NotificationMute[]>([]);
  const [inboxPreferences, setInboxPreferences] = useState<
    Record<string, Partial<NotificationPreferences>>
  >({});
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [pushPermission, setPushPermission] = useState<PermissionState>(notificationPermission);
  const pageRef = useRef(1);
  const timersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  // Latest values for the socket handler without resubscribing per render.
  const selectedIdRef = useRef(selectedId);
  const itemsRef = useRef<CrmNotification[]>([]);
  const itemsLoadedRef = useRef(false);
  const preferencesRef = useRef<NotificationPreferences | null>(null);
  useEffect(() => {
    selectedIdRef.current = selectedId;
    itemsRef.current = items;
    preferencesRef.current = preferences;
  });

  // Spend the first gesture unlocking playback, so the tone for a notification
  // arriving on an idle tab is not the one autoplay policy swallows. The
  // callback runs at gesture time, by when the preferences have usually landed.
  //
  // Only the tones this surface can actually sound: embedded, the message tone
  // is unreachable (raiseAlert returns first) and the ringtone is not, so
  // priming both there would spend a gesture playing — muted, but playing — an
  // element nothing will ever ask for again. That is a sound with no
  // notification behind it and no way for an agent to explain it.
  const embedded = source.embedded;
  useEffect(() => {
    primeAudio(() => {
      const prefs = preferencesRef.current;
      const ringtone = isAlertTone(prefs?.call_ringtone)
        ? prefs.call_ringtone
        : DEFAULT_CALL_RINGTONE;
      if (embedded) return [ringtone];
      return [
        isAlertTone(prefs?.message_tone) ? prefs.message_tone : DEFAULT_MESSAGE_TONE,
        ringtone,
      ];
    });
  }, [embedded]);

  const refetchUnread = useCallback(() => {
    source.api.notificationUnreadCount().then(
      (result) => setUnread(result.count),
      () => {},
    );
  }, [source]);

  const loadMutes = useCallback(() => {
    source.api.listNotificationMutes().then(
      (rows) => setMutes(rows),
      () => {},
    );
  }, [source]);

  /**
   * After an outage the badge is re-read, and the list has to be too: a
   * notification created while the socket was down is in the count and not
   * in the panel, and a bell that says 3 over a list of 2 reads as broken.
   * The newest page, merged by id — a row already held takes the fresh copy,
   * which is also how a `readAt` set at another desk reaches this one.
   */
  const resyncItems = useCallback(() => {
    source.api.listNotifications({ page: 1 }).then(
      (result) => {
        setItems((current) => {
          const byId = new Map(current.map((row) => [row.id, row]));
          for (const row of result.payload) byId.set(row.id, row);
          return [...byId.values()].sort((a, b) => b.id - a.id);
        });
      },
      () => {},
    );
  }, [source]);

  // `refreshNonce` re-runs this whole read on the module-wide Refresh. The bell
  // is socket-fed, so it is only ever wrong when the socket was — which is the
  // case the button is for.
  useEffect(() => {
    let cancelled = false;
    pageRef.current = 1;
    itemsLoadedRef.current = false;
    // Fetch-on-mount is this provider's contract — same as useLoad.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    Promise.all([
      source.api.listNotifications({ page: 1 }),
      source.api.notificationUnreadCount(),
      source.api.listNotificationMutes().catch(() => []),
      source.api.getNotificationSettings().catch(() => null),
      source.api.getNotificationInboxSettings().catch(() => ({})),
    ]).then(
      ([list, count, muteRows, settings, inboxSettings]) => {
        if (cancelled) return;
        itemsLoadedRef.current = true;
        setItems(list.payload);
        setHasMore(list.payload.length === PAGE_SIZE);
        setUnread(count.count);
        setMutes(muteRows);
        // Read through the ref too: the socket handler may fire before the
        // next render commits, and a missing tone is a silent notification.
        preferencesRef.current = settings?.preferences ?? null;
        setPreferences(settings?.preferences ?? null);
        setInboxPreferences(inboxSettings ?? {});
        setLoading(false);
      },
      (e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.kind === 'forbidden') setDenied(true);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [source, refreshNonce]);

  const dismissToast = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer) clearTimeout(timer);
    timersRef.current.delete(id);
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback(
    (n: CrmNotification) => {
      setToasts((current) => [n, ...current].slice(0, MAX_TOASTS));
      const timer = setTimeout(() => dismissToast(n.id), TOAST_MS);
      timersRef.current.set(n.id, timer);
    },
    [dismissToast],
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  /**
   * Alert legs for one arriving notification, in the order they escalate:
   * tone, then an OS banner when the tab is not the one being looked at. Both
   * are skipped when embedded — see the module docblock.
   */
  const raiseAlert = useCallback(
    (n: CrmNotification) => {
      if (source.embedded) return;
      const prefs = preferencesRef.current;
      if (prefs?.sound_enabled !== false) {
        const tone = isAlertTone(prefs?.message_tone) ? prefs.message_tone : DEFAULT_MESSAGE_TONE;
        playTone(tone);
      }
      if (!document.hidden) return;
      showSystemNotification({
        title: notificationTitle(n),
        // Same shape as the FCM body, so one notification reads identically
        // whether it arrived over the socket or as a background push.
        body: [notificationOrigin(n), n.snapshot.preview].filter(Boolean).join(' — '),
        // One banner per conversation — a busy thread must not stack a dozen.
        tag: `crm-conversation-${n.conversationDisplayId ?? n.id}`,
        onClick: () => {
          if (n.conversationDisplayId !== null) onOpenConversation(n.conversationDisplayId);
        },
      });
    },
    [source, onOpenConversation],
  );

  // Socket feed: prepend, badge, toast (unless the conversation is on screen),
  // alert, and relay to the dashboard when embedded.
  useEffect(() => {
    return source.subscribe((event) => {
      if (event.name === 'notification.created') {
        const n = event.payload as CrmNotification;
        // A frame socket.io redelivered around a reconnect is the same row
        // twice. The list dedups by id; the badge has to as well, or it reads
        // one higher than the rows the panel can show until the next re-read.
        if (itemsRef.current.some((row) => row.id === n.id)) return;
        setItems((current) =>
          current.some((row) => row.id === n.id) ? current : [n, ...current],
        );
        setUnread((current) => current + 1);
        source.notifyHost?.(n);
        // The dashboard toasts it (see the module docblock).
        if (source.embedded) return;
        if (n.conversationDisplayId !== selectedIdRef.current || document.hidden) {
          pushToast(n);
          raiseAlert(n);
        }
        return;
      }
      if (event.name === 'reconnected') {
        refetchUnread();
        loadMutes();
        resyncItems();
      }
    });
  }, [source, pushToast, refetchUnread, loadMutes, resyncItems, raiseAlert]);

  /**
   * Subscribe this browser to background push once the agent has granted
   * permission — the leg that survives the tab being closed. Standalone only,
   * and re-run when the permission flips (the settings card bumps `denied`
   * through a state change of its own by reloading preferences).
   */
  useEffect(() => {
    if (source.embedded || denied || pushPermission !== 'granted') return;
    try {
      // Throws without a session — the fixtures preview mounts the module
      // with no tenant at all, and push is meaningless there anyway.
      void registerBackgroundPush(getTenant(), source.api.registerPushDevice);
    } catch {
      // No tenant: nothing to subscribe to.
    }
  }, [source, denied, pushPermission]);

  /** Prompt for OS notifications, then subscribe push. Must run from a click. */
  const enablePush = useCallback(async (): Promise<PermissionState> => {
    const result = await requestNotificationPermission();
    setPushPermission(result);
    return result;
  }, []);

  /**
   * Save a preferences patch. Optimistic, with a rollback — the server merges
   * in Postgres and answers with the merged blob, which then wins.
   */
  const savePreferences = useCallback(
    async (patch: Partial<NotificationPreferences>) => {
      const previous = preferencesRef.current;
      if (previous) {
        const next = { ...previous, ...patch };
        preferencesRef.current = next;
        setPreferences(next);
      }
      try {
        const result = await source.api.putNotificationSettings(patch);
        preferencesRef.current = result.preferences;
        setPreferences(result.preferences);
      } catch (error) {
        preferencesRef.current = previous;
        setPreferences(previous);
        throw error;
      }
    },
    [source],
  );

  const markRead = useCallback(
    (id: number) => {
      // Checked against the ref, not inside the setItems updater — updaters run
      // at render time, after this function has already returned.
      const row = itemsRef.current.find((candidate) => candidate.id === id);
      if (!row || row.readAt) return;
      const readAt = new Date().toISOString();
      setItems((current) =>
        current.map((candidate) => (candidate.id === id ? { ...candidate, readAt } : candidate)),
      );
      setUnread((current) => Math.max(0, current - 1));
      source.api.markNotificationRead(id).then(
        // After the write, not before: the dashboard re-reads its count when
        // told, and a nudge that beat the write would read the old one.
        () => source.notifyHostRead?.([id]),
        () => refetchUnread(),
      );
    },
    [source, refetchUnread],
  );

  const markAllRead = useCallback(() => {
    const now = new Date().toISOString();
    const ids = itemsRef.current.filter((row) => !row.readAt).map((row) => row.id);
    setItems((current) => current.map((row) => (row.readAt ? row : { ...row, readAt: now })));
    setUnread(0);
    setToasts([]);
    source.api.markAllNotificationsRead().then(
      () => source.notifyHostRead?.(ids),
      () => refetchUnread(),
    );
  }, [source, refetchUnread]);

  // Opening a conversation clears its notifications — the badge must not keep
  // nagging about something the agent is already looking at.
  useEffect(() => {
    if (selectedId === null || !itemsLoadedRef.current) return;
    for (const row of items) {
      if (!row.readAt && row.conversationDisplayId === selectedId) markRead(row.id);
    }
    setToasts((current) => current.filter((t) => t.conversationDisplayId !== selectedId));
  }, [selectedId, items, markRead]);

  const loadMore = useCallback(() => {
    const nextPage = pageRef.current + 1;
    setLoadingMore(true);
    source.api.listNotifications({ page: nextPage }).then(
      (result) => {
        setLoadingMore(false);
        pageRef.current = nextPage;
        setItems((current) => {
          const seen = new Set(current.map((row) => row.id));
          return [...current, ...result.payload.filter((row) => !seen.has(row.id))];
        });
        setHasMore(result.payload.length === PAGE_SIZE);
      },
      () => setLoadingMore(false),
    );
  }, [source]);

  const openNotification = useCallback(
    (n: CrmNotification) => {
      markRead(n.id);
      dismissToast(n.id);
      if (n.conversationDisplayId !== null) onOpenConversation(n.conversationDisplayId);
    },
    [markRead, dismissToast, onOpenConversation],
  );

  const muteFor = useCallback(
    (scope: 'inbox' | 'conversation', targetId: number | null) => {
      if (targetId === null) return null;
      return (
        mutes.find(
          (m) =>
            m.scope === scope &&
            (scope === 'inbox' ? m.inboxId === targetId : m.conversationDisplayId === targetId),
        ) ?? null
      );
    },
    [mutes],
  );

  const mute = useCallback(
    async (body: {
      scope: 'inbox' | 'conversation';
      inboxId?: number;
      conversationDisplayId?: number;
      until?: string | null;
    }) => {
      await source.api.createNotificationMute(body);
      loadMutes();
    },
    [source, loadMutes],
  );

  const unmute = useCallback(
    async (muteId: number) => {
      await source.api.deleteNotificationMute(muteId);
      setMutes((current) => current.filter((m) => m.id !== muteId));
    },
    [source],
  );

  /**
   * Per-channel overrides. Optimistic like the global settings, and rolled back
   * on failure — a toggle that silently reverts is better than one that lies.
   */
  const saveInboxPreferences = useCallback(
    async (inboxId: number, patch: Partial<NotificationPreferences>) => {
      const previous = inboxPreferences;
      setInboxPreferences((current) => ({
        ...current,
        [String(inboxId)]: { ...(current[String(inboxId)] ?? {}), ...patch },
      }));
      try {
        const result = await source.api.putNotificationInboxSettings(inboxId, patch);
        setInboxPreferences((current) => ({
          ...current,
          [String(inboxId)]: result.preferences,
        }));
      } catch (error) {
        setInboxPreferences(previous);
        throw error;
      }
    },
    [source, inboxPreferences],
  );

  const clearInboxPreferences = useCallback(
    async (inboxId: number) => {
      await source.api.clearNotificationInboxSettings(inboxId);
      setInboxPreferences((current) => {
        const next = { ...current };
        delete next[String(inboxId)];
        return next;
      });
    },
    [source],
  );

  const value = useMemo<NotificationsState>(
    () => ({
      unread,
      items,
      loading,
      hasMore,
      loadingMore,
      loadMore,
      denied,
      hostedByDashboard: source.embedded,
      toasts,
      dismissToast,
      markRead,
      markAllRead,
      openNotification,
      mutes,
      inboxPreferences,
      saveInboxPreferences,
      clearInboxPreferences,
      muteFor,
      mute,
      unmute,
      preferences,
      savePreferences,
      // The dashboard owns OS notifications on its own origin; offering the
      // prompt inside its iframe would ask for permission the frame can't use.
      supportsPush: !source.embedded && pushPermission !== 'unsupported',
      pushPermission,
      enablePush,
    }),
    [
      unread,
      items,
      loading,
      hasMore,
      loadingMore,
      loadMore,
      denied,
      toasts,
      dismissToast,
      markRead,
      markAllRead,
      openNotification,
      mutes,
      inboxPreferences,
      saveInboxPreferences,
      clearInboxPreferences,
      muteFor,
      mute,
      unmute,
      preferences,
      savePreferences,
      source,
      pushPermission,
      enablePush,
    ],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
